/**
 * A treasure item's icon at whatever size the caller asks for, falling back to
 * the generic prize glyph when there is none (no icon, or an unlinked reward).
 *
 * Takes a platform media URL (absolute) or an old award's root-relative
 * `/assets/...` path; both resolve through `resolveServerUrl`.
 */

import { resolveServerUrl } from '../../../../utils/serverUrl';

interface ItemIconProps {
  iconUrl?: string | null;
  /** Item name, used as the image's alt text. */
  name: string;
  /** Box size in px. */
  size?: number;
}

export function ItemIcon({ iconUrl, name, size = 24 }: ItemIconProps) {
  // Sized inline rather than by a class: the caller picks the box, and a
  // fixed-size icon in a flex row must not be squeezed by a long item name.
  const box = { width: size, height: size, flexShrink: 0 } as const;

  if (!iconUrl) {
    return (
      <span
        className="d-flex align-items-center justify-content-center"
        style={{ ...box, fontSize: size * 0.75 }}
        aria-hidden="true"
      >
        🎁
      </span>
    );
  }

  return (
    <img
      className="rounded-2xs"
      style={{ ...box, objectFit: 'contain' }}
      src={resolveServerUrl(iconUrl)}
      alt={name}
      loading="lazy"
    />
  );
}
